import React, { useMemo, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useDispatch } from 'react-redux';
import type { IContest, SubmissionCategory } from '../../../types/global/helper';
import type { IAwardCategory } from '../../../redux/contest/types';
import { deleteAwardCategory, updateAwardCategory } from '../../../redux/contest/actions';
import CreateAwardCategoryDialog from '../../../components/ScreenContainer/ContestContainer/Tabs/CreateAwardCategoryDialog';
import { DialogueSheet } from '../../../components/DialogueSheet/DialogueSheet';
import {
    awardsForSubmissionCategory,
    buildAwardLookup,
    isConfiguredAward,
    prepareAwardCategoryForEdit,
    resolveAwardPlacementForDelete,
    resolveContestCategoryIndex,
    tierLabelForAward,
    type AwardCategoryEditShape,
} from './contestCategoryWizardUtils';
import { RichTextViewer } from '../../../components/Common/RichTextViewer';
import { editTriggerSx, sectionShellSx } from './BrandContestDetailsDrawerUi';
import { htmlLooksLikeMarkup, plainTextFromHtml } from '../contestWizard/richTextUtils';
import { formatAwardPrizeForDisplay, sumAwardPrizeCents } from '../contestWizard/buildAwardCategoryPayload';
import { editorialHeadlineSx, midnight } from '../contestWizard/midnightStyles';
import {
    BrandConvertSubmissionModeDialog,
    type ConvertSubmissionModeDirection,
} from './BrandConvertSubmissionModeDialog';
import { flatFeeMajorUnits, resolveCategorySubmissionMode } from '../../../utils/contestSubmissionMode';

const CATEGORIES_LOCKED_MESSAGE = 'Campaign already has submissions. You cannot change submission categories.';

const CATEGORY_AWARDS_EMPTY_MESSAGE = 'No awards have been set for this category.';

function formatTierFee(tier: {
    label: string;
    fee?: string | number;
    bare_amount?: number;
    formatted_for_display?: string;
}) {
    if (tier.formatted_for_display) return `${tier.label} · ${tier.formatted_for_display}`;
    const dollars =
        tier.bare_amount != null
            ? tier.bare_amount / 100
            : typeof tier.fee === 'number'
            ? tier.fee
            : parseFloat(String(tier.fee)) || 0;
    return `${tier.label} · $${dollars}`;
}

function sortSubmissionCategoriesForDisplay(categories: SubmissionCategory[]): SubmissionCategory[] {
    return [...categories].sort((a, b) => {
        if (a._id && b._id) {
            return String(a._id).localeCompare(String(b._id));
        }
        if (a._id) return -1;
        if (b._id) return 1;
        return (a.category_name || '').localeCompare(b.category_name || '', undefined, {
            sensitivity: 'base',
        });
    });
}

export function categoryHasConfiguredAwards(
    category: SubmissionCategory,
    awardCategories?: IAwardCategory[],
    awardById?: Map<string, IAwardCategory>,
): boolean {
    const placements = category.prize_structure || [];
    if (placements.some(pl => (pl.payouts || []).length > 0)) return true;
    const lookup = awardById ?? buildAwardLookup(awardCategories);
    return awardsForSubmissionCategory(category, awardCategories, lookup).some(isConfiguredAward);
}

function AwardPrizeChip({
    text,
    canEdit,
    canDelete,
    onEdit,
    onDelete,
}: {
    text: string;
    canEdit?: boolean;
    canDelete?: boolean;
    onEdit?: () => void;
    onDelete?: () => void;
}) {
    const showDelete = Boolean(canDelete && onDelete);
    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                borderRadius: '8px',
                bgcolor: midnight.surfaceContainer,
                border: '1px solid transparent',
                pr: showDelete ? 0.25 : 0,
                ...(canEdit
                    ? {
                          '&:hover': {
                              borderColor: 'rgba(255,201,101,0.35)',
                              bgcolor: midnight.surfaceContainerHigh,
                          },
                      }
                    : {}),
            }}>
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.75,
                    ...(canEdit && onEdit
                        ? {
                              cursor: 'pointer',
                          }
                        : {}),
                }}
                onClick={canEdit && onEdit ? onEdit : undefined}
                role={canEdit && onEdit ? 'button' : undefined}
                tabIndex={canEdit && onEdit ? 0 : undefined}
                onKeyDown={
                    canEdit && onEdit
                        ? (e: React.KeyboardEvent) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onEdit();
                              }
                          }
                        : undefined
                }>
                <Typography
                    sx={{
                        fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                        fontSize: 13,
                        fontWeight: 600,
                        color: midnight.onSurface,
                    }}>
                    {text}
                </Typography>
                {canEdit && onEdit ? (
                    <EditOutlinedIcon sx={{ fontSize: 14, color: midnight.onSurfaceVariant }} />
                ) : null}
            </Box>
            {showDelete ? (
                <IconButton
                    size="small"
                    aria-label="Remove prize"
                    onClick={e => {
                        e.stopPropagation();
                        onDelete?.();
                    }}
                    sx={{ color: midnight.onSurfaceVariant, '&:hover': { color: '#ff8a65' } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
            ) : null}
        </Box>
    );
}

function CategoryAwardsSummary({
    category,
    awardById,
    linkedAwards,
    canEdit,
    canDelete,
    onEditAward,
    onDeleteAward,
}: {
    category: SubmissionCategory;
    awardById: Map<string, IAwardCategory>;
    linkedAwards: IAwardCategory[];
    canEdit?: boolean;
    canDelete?: boolean;
    onEditAward?: (award: IAwardCategory) => void;
    onDeleteAward?: (award: IAwardCategory) => void;
}) {
    const tierLabels = (category.tier_fee || []).map(t => t.label?.trim()).filter(Boolean) as string[];
    const placements = category.prize_structure || [];
    const hasStructurePayouts = placements.some(pl => (pl.payouts || []).length > 0);

    if (!hasStructurePayouts) {
        const configured = linkedAwards.filter(isConfiguredAward);
        if (!configured.length) {
            return (
                <Typography sx={{ color: midnight.onSurfaceVariant, fontStyle: 'italic', fontSize: 15 }}>
                    {CATEGORY_AWARDS_EMPTY_MESSAGE}
                </Typography>
            );
        }
        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {configured.map(award => {
                    const label = tierLabelForAward(category, award);
                    const cash = formatAwardPrizeForDisplay(award) || null;
                    const extras = (award.additional_prize || []).filter(Boolean);
                    const text = [label, cash, ...extras].filter(Boolean).join(' · ');
                    return (
                        <AwardPrizeChip
                            key={award._id}
                            text={text}
                            canEdit={canEdit && Boolean(onEditAward)}
                            canDelete={canDelete && Boolean(onDeleteAward)}
                            onEdit={() => onEditAward?.(award)}
                            onDelete={canDelete && onDeleteAward ? () => onDeleteAward(award) : undefined}
                        />
                    );
                })}
            </Box>
        );
    }

    if (!categoryHasConfiguredAwards(category, undefined, awardById)) {
        return (
            <Typography sx={{ color: midnight.onSurfaceVariant, fontStyle: 'italic', fontSize: 15 }}>
                {CATEGORY_AWARDS_EMPTY_MESSAGE}
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {placements.map((pl, pi) => (
                <Box key={`${pi}-${pl.position}`}>
                    <Typography
                        sx={{
                            fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                            fontWeight: 700,
                            fontSize: 13,
                            color: midnight.onSurface,
                            mb: 0.5,
                        }}>
                        {pl.position}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {(pl.payouts || []).map((p, oi) => {
                            const award = typeof p === 'string' ? awardById.get(p) : (p as IAwardCategory);
                            const tierLabel = tierLabels[oi] || tierLabels[0];
                            const label = tierLabel || award?.tier_name || award?.category_title || 'Prize';
                            const cash = award ? formatAwardPrizeForDisplay(award) || null : null;
                            const extras = (award?.additional_prize || []).filter(Boolean);
                            const text = [label, cash, ...extras].filter(Boolean).join(' · ');
                            if (!award?._id) {
                                return <AwardPrizeChip key={`${oi}-inline`} text={text} />;
                            }
                            return (
                                <AwardPrizeChip
                                    key={award._id}
                                    text={text}
                                    canEdit={canEdit && Boolean(onEditAward)}
                                    canDelete={canDelete && Boolean(onDeleteAward)}
                                    onEdit={() => onEditAward?.(award)}
                                    onDelete={canDelete && onDeleteAward ? () => onDeleteAward(award) : undefined}
                                />
                            );
                        })}
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

export interface BrandContestSubmissionCategoriesSectionProps {
    contest: IContest;
    hasSubmissions?: boolean;
    onEditCategory?: (categoryIndex: number) => void;
    onCreateCategory?: () => void;
}

export function BrandContestSubmissionCategoriesSection({
    contest,
    hasSubmissions = false,
    onEditCategory,
    onCreateCategory,
}: BrandContestSubmissionCategoriesSectionProps) {
    const submissionCategories = useMemo(
        () => sortSubmissionCategoriesForDisplay(contest.categories ?? []),
        [contest.categories],
    );
    const [convertState, setConvertState] = useState<{
        direction: ConvertSubmissionModeDirection;
        category: SubmissionCategory;
    } | null>(null);

    return (
        <Box sx={sectionShellSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, gap: 2 }}>
                <Typography sx={{ ...editorialHeadlineSx, fontSize: 22, color: midnight.onSurface }}>
                    Submission categories
                </Typography>
                {!hasSubmissions && (onEditCategory || onCreateCategory) ? (
                    <Button
                        type="button"
                        variant="text"
                        size="small"
                        onClick={() => {
                            if (submissionCategories.length === 1 && onEditCategory) {
                                onEditCategory(
                                    resolveContestCategoryIndex(contest.categories, submissionCategories[0]),
                                );
                            } else {
                                onCreateCategory?.();
                            }
                        }}
                        sx={editTriggerSx}>
                        <EditOutlinedIcon sx={{ fontSize: 16 }} />
                        {submissionCategories.length === 1
                            ? 'Edit category'
                            : submissionCategories.length > 1
                            ? 'Add category'
                            : 'Add category'}
                    </Button>
                ) : null}
            </Box>
            {hasSubmissions ? (
                <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 13, mb: 2 }}>
                    {CATEGORIES_LOCKED_MESSAGE} You can still switch between flat fee and tiered pricing below.
                </Typography>
            ) : null}
            {submissionCategories.length ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {submissionCategories.map((cat, idx) => {
                        const mode = resolveCategorySubmissionMode(cat, contest);
                        const canConvertToRegular = mode === 'tier' && (cat.tier_fee?.length ?? 0) > 0;
                        const canConvertToTier = mode === 'regular';

                        return (
                            <Box
                                key={cat._id ?? `cat-${idx}`}
                                sx={{
                                    bgcolor: midnight.surfaceContainer,
                                    borderRadius: '10px',
                                    p: 2,
                                    border: '1px solid rgba(72,72,71,0.2)',
                                }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: 1,
                                        mb: 1,
                                    }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: 15,
                                                color: midnight.onSurface,
                                            }}>
                                            {cat.category_name}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                fontWeight: 700,
                                                letterSpacing: '0.08em',
                                                textTransform: 'uppercase',
                                                color: midnight.primary,
                                                mt: 0.5,
                                            }}>
                                            {mode === 'tier' ? 'Tiered pricing' : 'Flat fee (regular)'}
                                        </Typography>
                                    </Box>
                                    {!hasSubmissions && onEditCategory ? (
                                        <Button
                                            type="button"
                                            size="small"
                                            onClick={() =>
                                                onEditCategory(resolveContestCategoryIndex(contest.categories, cat))
                                            }
                                            sx={{
                                                ...editTriggerSx,
                                                minWidth: 0,
                                                py: 0,
                                            }}>
                                            Edit
                                        </Button>
                                    ) : null}
                                </Box>
                                {cat.category_description?.trim() ? (
                                    htmlLooksLikeMarkup(cat.category_description) ? (
                                        <Box sx={{ mb: 1.5, fontSize: 13, color: midnight.onSurfaceVariant }}>
                                            <RichTextViewer
                                                content={cat.category_description}
                                                textColor={midnight.onSurfaceVariant}
                                            />
                                        </Box>
                                    ) : (
                                        <Typography
                                            sx={{
                                                fontSize: 13,
                                                color: midnight.onSurfaceVariant,
                                                lineHeight: 1.5,
                                                mb: 1.5,
                                            }}>
                                            {plainTextFromHtml(cat.category_description)}
                                        </Typography>
                                    )
                                ) : null}
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                                    {mode === 'tier' ? (
                                        (cat.tier_fee || []).map((tier, ti) => (
                                            <Box
                                                key={`${ti}-${tier.label}`}
                                                sx={{
                                                    px: 1.25,
                                                    py: 0.5,
                                                    borderRadius: 999,
                                                    bgcolor: midnight.surfaceHighest,
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    color: midnight.onSurface,
                                                }}>
                                                {formatTierFee(tier)}
                                            </Box>
                                        ))
                                    ) : (
                                        <Box
                                            sx={{
                                                px: 1.25,
                                                py: 0.5,
                                                borderRadius: 999,
                                                bgcolor: midnight.surfaceHighest,
                                                fontSize: 11,
                                                fontWeight: 600,
                                                color: midnight.onSurface,
                                            }}>
                                            {cat.with_fee === false
                                                ? 'Free'
                                                : cat.formatted_for_display || `$${flatFeeMajorUnits(cat).toFixed(2)}`}
                                        </Box>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {canConvertToRegular ? (
                                        <Button
                                            size="small"
                                            onClick={() => setConvertState({ direction: 'to-regular', category: cat })}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                fontSize: 12,
                                                color: midnight.primary,
                                            }}>
                                            Use a single fee (no tiers)
                                        </Button>
                                    ) : null}
                                    {canConvertToTier ? (
                                        <Button
                                            size="small"
                                            onClick={() => setConvertState({ direction: 'to-tier', category: cat })}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                fontSize: 12,
                                                color: midnight.primary,
                                            }}>
                                            Enable tiered pricing
                                        </Button>
                                    ) : null}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            ) : (
                <Typography sx={{ color: midnight.onSurfaceVariant, fontStyle: 'italic' }}>
                    No submission categories yet. Add one to define tiers, fees, and awards.
                </Typography>
            )}

            {convertState ? (
                <BrandConvertSubmissionModeDialog
                    open
                    direction={convertState.direction}
                    contest={contest}
                    category={convertState.category}
                    hasPaidOrAnySubmissions={hasSubmissions}
                    onClose={() => setConvertState(null)}
                />
            ) : null}
        </Box>
    );
}

export interface BrandContestAwardsPrizesSectionProps {
    contest: IContest;
    awardCategories?: IAwardCategory[];
    hasSubmissions?: boolean;
}

export function BrandContestAwardsPrizesSection({
    contest,
    awardCategories,
    hasSubmissions = false,
}: BrandContestAwardsPrizesSectionProps) {
    const dispatch = useDispatch();
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedAward, setSelectedAward] = useState<AwardCategoryEditShape | null>(null);
    const [awardPendingDelete, setAwardPendingDelete] = useState<{
        award: IAwardCategory;
        submissionCategory: SubmissionCategory;
    } | null>(null);

    const submissionCategories = useMemo(
        () => sortSubmissionCategoriesForDisplay(contest.categories ?? []),
        [contest.categories],
    );
    const awardById = useMemo(() => buildAwardLookup(awardCategories), [awardCategories]);
    const canEditAwards = !hasSubmissions;
    /** Soft-delete prizes even after submissions (e.g. drop Audience Award when voting is removed). */
    const canDeleteAwards = true;

    const handleEditAward = (award: IAwardCategory, submissionCategory: SubmissionCategory) => {
        setSelectedAward(prepareAwardCategoryForEdit(award, submissionCategory));
        setEditDialogOpen(true);
    };

    const handleDeleteAward = (award: IAwardCategory, submissionCategory: SubmissionCategory) => {
        setAwardPendingDelete({ award, submissionCategory });
        setDeleteDialogOpen(true);
    };

    const confirmDeleteAward = () => {
        if (!awardPendingDelete || !contest._id) return;
        const { award, submissionCategory } = awardPendingDelete;
        const position = resolveAwardPlacementForDelete(award, submissionCategory);

        dispatch(
            deleteAwardCategory({
                documentId: award._id,
                payload: {
                    categoryId: submissionCategory._id,
                    contestId: contest._id,
                    position,
                },
            }),
        );
        setDeleteDialogOpen(false);
        setAwardPendingDelete(null);
    };

    const handleSaveAward = (awardData: Record<string, unknown>) => {
        if (!selectedAward?._id) return;

        const payload = { ...awardData };
        const oldPosition = selectedAward.position ?? selectedAward.tier_position;
        const oldTierLabel =
            selectedAward.tier_label ??
            tierLabelForAward(
                submissionCategories.find(c => c._id === selectedAward.category) ?? submissionCategories[0],
                selectedAward,
            );

        // Preserve tier_fee ObjectId on tier_name (dialog already resolved label → id when possible).
        if (payload.tier_label && payload.category && !payload.tier_name) {
            payload.tier_position = payload.position;
        }

        dispatch(
            updateAwardCategory({
                documentId: selectedAward._id,
                payload,
                additionalPayload: {
                    oldPosition,
                    oldTierLabel,
                },
            }),
        );
        setEditDialogOpen(false);
        setSelectedAward(null);
    };

    return (
        <Box sx={sectionShellSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                <Typography sx={{ ...editorialHeadlineSx, fontSize: 22, color: midnight.onSurface }}>
                    Awards & prizes
                </Typography>
            </Box>
            {canEditAwards || canDeleteAwards ? (
                <Typography sx={{ fontSize: 12, color: midnight.onSurfaceVariant, mb: 2.5 }}>
                    {canEditAwards
                        ? 'Click a prize to edit cash amount, placement, tier, or additional perks. Use the trash icon to remove a prize.'
                        : 'Use the trash icon to remove prizes you no longer need (for example Audience Award after dropping voting).'}
                </Typography>
            ) : null}
            {submissionCategories.length ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {submissionCategories.map((cat, idx) => (
                        <Box key={`aw-${cat._id ?? idx}`}>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    color: midnight.onSurfaceVariant,
                                    mb: 1,
                                }}>
                                {cat.category_name}
                            </Typography>
                            <CategoryAwardsSummary
                                category={cat}
                                awardById={awardById}
                                linkedAwards={awardsForSubmissionCategory(cat, awardCategories, awardById)}
                                canEdit={canEditAwards}
                                canDelete={canDeleteAwards}
                                onEditAward={award => handleEditAward(award, cat)}
                                onDeleteAward={award => handleDeleteAward(award, cat)}
                            />
                        </Box>
                    ))}
                </Box>
            ) : (
                <Typography sx={{ color: midnight.onSurfaceVariant, fontStyle: 'italic' }}>
                    Awards appear after you configure a submission category.
                </Typography>
            )}

            <CreateAwardCategoryDialog
                open={editDialogOpen}
                visualVariant="brand"
                onClose={() => {
                    setEditDialogOpen(false);
                    setSelectedAward(null);
                }}
                onSubmit={handleSaveAward}
                contest={contest}
                editAward={selectedAward}
            />

            <DialogueSheet
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setAwardPendingDelete(null);
                }}
                onAgreeClicked={confirmDeleteAward}
                onDisagreeClick={() => {
                    setDeleteDialogOpen(false);
                    setAwardPendingDelete(null);
                }}
                header="Delete prize"
                body={
                    awardPendingDelete
                        ? `Remove this prize from ${awardPendingDelete.submissionCategory.category_name}? This cannot be undone.`
                        : 'Remove this prize? This cannot be undone.'
                }
                positiveText="Delete"
                negativeText="Cancel"
            />
        </Box>
    );
}

export function submissionCategoryHubSummary(contest: IContest): string {
    const cats = contest.categories ?? [];
    if (!cats.length) return 'Add tiers, fees, and awards';
    if (cats.length === 1) return cats[0].category_name || '1 category';
    return `${cats.length} categories`;
}

export function awardsPrizesHubSummary(contest: IContest, awardCategories?: IAwardCategory[]): string {
    const cats = contest.categories ?? [];
    const configuredAwards = (awardCategories || []).filter(isConfiguredAward);
    const awardById = buildAwardLookup(awardCategories);

    if (!cats.length && !configuredAwards.length) return 'Set up after submission category';

    if (configuredAwards.length === 1) {
        const award = configuredAwards[0];
        const category = cats.find(c =>
            awardsForSubmissionCategory(c, awardCategories, awardById).some(a => a._id === award._id),
        );
        const label = category ? tierLabelForAward(category, award) : award.category_title || 'Prize';
        const cash = formatAwardPrizeForDisplay(award);
        const extras = (award.additional_prize || []).filter(Boolean);
        return [label, cash, ...extras.slice(0, 1)].filter(Boolean).join(' · ');
    }

    if (configuredAwards.length > 1) {
        const totalCents = sumAwardPrizeCents(configuredAwards);
        const total = totalCents > 0 ? formatAwardPrizeForDisplay({ prize: totalCents }) : '';
        return total ? `${configuredAwards.length} prizes · ${total}` : `${configuredAwards.length} prizes configured`;
    }

    if (!cats.length) return 'No prizes configured yet';
    const withAwards = cats.filter(c => categoryHasConfiguredAwards(c, awardCategories, awardById));
    if (!withAwards.length) return 'No prizes configured yet';
    return `${withAwards.length} categor${withAwards.length === 1 ? 'y' : 'ies'} with prizes`;
}
